import axios from "axios";
import { auth } from "../firebase/firebase";

const normalizeUrl = (value: string): string => {
  const trimmed = value.trim();
  return trimmed.endsWith("/") ? trimmed : `${trimmed}/`;
};

const extractConfiguredUrl = (value: string): string => {
  const trimmed = value.trim();
  const stripped = trimmed.replace(/^REACT_APP_BACKEND_BASE_URL\s*=\s*/i, "");
  return stripped.trim();
};

const isAbsoluteHttpUrl = (value: string): boolean =>
  /^https?:\/\//i.test(value.trim());

const resolveBackendBaseUrl = (): string => {
  const configuredRaw = process.env.REACT_APP_BACKEND_BASE_URL || "";
  const configured = extractConfiguredUrl(configuredRaw);
  const isDev = process.env.REACT_APP_ENVIRONMENT === "dev";

  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    const isProdHost = host === "superadmin.inflos.co";
    const looksLocalhost = /^https?:\/\/(localhost|127\.0\.0\.1)/i.test(configured);
    const isMalformedConfigured = configured.length > 0 && !isAbsoluteHttpUrl(configured);

    if (isProdHost && (!configured || looksLocalhost || isMalformedConfigured)) {
      return "https://superadmin.inflos.co/api/";
    }
  }

  if (configured && isAbsoluteHttpUrl(configured)) {
    return normalizeUrl(configured);
  }

  return isDev ? "http://localhost:1231/" : "https://superadmin.inflos.co/api/";
};

export let BACKEND_BASE_URL = resolveBackendBaseUrl();

/**
 * An axios instance for the admin API. Pass the signed-in user's token (any
 * non-empty value) to make an authenticated call: the actual bearer token is
 * read from Firebase just before each request, which hands back a cached
 * token and silently refreshes it when it's about to expire. A 401 is retried
 * once with a forced refresh (e.g. after the laptop slept past expiry), so
 * callers never need to track token lifetimes.
 */
const request = (token?: string | null) => {
  const axiosInstance = axios.create({
    baseURL: BACKEND_BASE_URL,
    timeout: 60000,
    headers: {
      Authorization: token ? `Bearer ${token}` : "null",
      "Content-Type": "application/json",
      accept: "application/json",
    },
  });

  if (token) {
    axiosInstance.interceptors.request.use(async (config) => {
      const user = auth.currentUser;
      if (user) config.headers.Authorization = `Bearer ${await user.getIdToken()}`;
      return config;
    });
    axiosInstance.interceptors.response.use(undefined, async (err) => {
      const config = err?.config;
      const user = auth.currentUser;
      if (err?.response?.status === 401 && config && !config._retriedWithFreshToken && user) {
        config._retriedWithFreshToken = true;
        config.headers.Authorization = `Bearer ${await user.getIdToken(true)}`;
        return axiosInstance.request(config);
      }
      return Promise.reject(err);
    });
  }

  return axiosInstance;
};

export { request };
